import React, { useMemo } from "react";
import { Link } from "react-router-dom";

function clean(value) {
    return String(value || "").trim();
}

function safeProjectPart(value, fallback = "custom") {
    const cleanValue = clean(value)
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");

    return (cleanValue || fallback).slice(0, 32);
}

function selectedModuleList(modules = {}) {
    return Object.keys(modules)
        .filter((key) => Boolean(modules[key]))
        .sort();
}

function formatTokenType(tokenType) {
    const cleanType = clean(tokenType).toUpperCase();
    if (cleanType === "ERC721") return "ERC721 NFT";
    if (cleanType === "ERC1155") return "ERC1155 Multi-token";
    return "ERC20 Token";
}

export default function CustomizeSolidityButton({
    tokenType,
    tokenName,
    tokenSymbol,
    erc20Decimals,
    erc20InitialSupply,
    baseURI,
    royaltyFeeNumerator,
    royaltyReceiver,
    modules,
    selectedNetwork,
    deploySelectedNetworks,
    disabled = false,
    disabledReason = "",
}) {
    const selectedModules = useMemo(() => selectedModuleList(modules), [modules]);
    const customProjectId = useMemo(() => {
        const typePart = safeProjectPart(tokenType || "ERC20", "erc20");
        const namePart = safeProjectPart(tokenName || "token", "token");
        const symbolPart = safeProjectPart(tokenSymbol || "symbol", "symbol");
        const modulePart =
            selectedModules.length > 0
                ? selectedModules.map((item) => safeProjectPart(item)).join("_")
                : "base";

        return `custom_${typePart}_${modulePart}_${namePart}_${symbolPart}`.slice(
            0,
            96,
        );
    }, [tokenType, tokenName, tokenSymbol, selectedModules]);

    const search = useMemo(() => {
        const params = new URLSearchParams();

        params.set("type", clean(tokenType) || "ERC20");
        params.set("projectId", customProjectId);

        if (clean(tokenName)) params.set("name", clean(tokenName));
        if (clean(tokenSymbol)) params.set("symbol", clean(tokenSymbol));
        if (clean(erc20Decimals)) params.set("decimals", clean(erc20Decimals));
        if (clean(erc20InitialSupply)) {
            params.set("initialSupply", clean(erc20InitialSupply));
        }

        if (clean(baseURI)) params.set("baseURI", clean(baseURI));
        if (clean(royaltyFeeNumerator)) {
            params.set("royaltyBps", clean(royaltyFeeNumerator));
        }
        if (clean(royaltyReceiver)) {
            params.set("royaltyReceiver", clean(royaltyReceiver));
        }

        if (selectedModules.length > 0) {
            params.set("modules", selectedModules.join(","));
        }

        const firstNetwork =
            Array.isArray(deploySelectedNetworks) && deploySelectedNetworks.length
                ? deploySelectedNetworks[0]
                : selectedNetwork;

        if (clean(firstNetwork)) {
            params.set("network", clean(firstNetwork));
        }

        return params.toString();
    }, [
        customProjectId,
        tokenType,
        tokenName,
        tokenSymbol,
        erc20Decimals,
        erc20InitialSupply,
        baseURI,
        royaltyFeeNumerator,
        royaltyReceiver,
        selectedModules,
        selectedNetwork,
        deploySelectedNetworks,
    ]);

    const state = useMemo(
        () => ({
            customSeed: {
                projectId: customProjectId,
                tokenType: clean(tokenType) || "ERC20",
                tokenName: clean(tokenName),
                tokenSymbol: clean(tokenSymbol),
                erc20Decimals: clean(erc20Decimals),
                erc20InitialSupply: clean(erc20InitialSupply),
                baseURI: clean(baseURI),
                royaltyFeeNumerator: clean(royaltyFeeNumerator),
                royaltyReceiver: clean(royaltyReceiver),
                modules: selectedModules,
                selectedNetwork: clean(selectedNetwork),
                deploySelectedNetworks: Array.isArray(deploySelectedNetworks)
                    ? deploySelectedNetworks
                    : [],
            },
        }),
        [
            customProjectId,
            tokenType,
            tokenName,
            tokenSymbol,
            erc20Decimals,
            erc20InitialSupply,
            baseURI,
            royaltyFeeNumerator,
            royaltyReceiver,
            selectedModules,
            selectedNetwork,
            deploySelectedNetworks,
        ],
    );

    const moduleLabel =
        selectedModules.length > 0 ? selectedModules.join(", ") : "base contract";

    return (
        <section className="relative overflow-hidden rounded-[2rem] border border-[#E8D7AD] bg-white p-1 shadow-[0_28px_80px_rgba(15,23,42,0.08)]">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_8%_0%,rgba(232,215,173,0.55),transparent_32%),radial-gradient(circle_at_90%_12%,rgba(15,23,42,0.08),transparent_30%),linear-gradient(135deg,rgba(255,249,234,0.95),rgba(255,255,255,0.92),rgba(248,250,252,0.95))]" />

            <div className="relative grid gap-6 rounded-[1.75rem] bg-white/60 p-6 backdrop-blur-sm lg:grid-cols-[1fr_280px] lg:items-center">
                <div>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#E8D7AD] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#9A7226] shadow-sm">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#9A7226]" />
                        Custom Solidity Studio
                    </div>

                    <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.04em] text-[#050713] sm:text-3xl">
                        Review, edit, and deploy your own Solidity
                    </h3>

                    <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600">
                        Start from your Studio configuration, customize the generated contract,
                        run safety checks, and deploy only after wallet review.
                    </p>

                    <div className="mt-5 grid gap-3 text-xs font-semibold text-slate-700 sm:grid-cols-3">
                        <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
                                    Standard
                                </div>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
                                    source
                                </span>
                            </div>
                            <div className="mt-2 text-sm text-[#050713]">
                                {formatTokenType(tokenType)}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
                                    Modules
                                </div>
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
                                    selected
                                </span>
                            </div>
                            <div className="mt-2 text-sm text-[#050713]">
                                {moduleLabel}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-[10px] uppercase tracking-[0.16em] text-slate-400">
                                    Safety
                                </div>
                                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700">
                                    guarded
                                </span>
                            </div>
                            <div className="mt-2 text-sm text-[#050713]">
                                Compile checks + preflight review
                            </div>
                        </div>
                    </div>

                    {disabled && disabledReason ? (
                        <p className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                            {disabledReason}
                        </p>
                    ) : null}
                </div>

                <div className="rounded-[1.5rem] border border-slate-200 bg-white/90 p-4 shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                    <div className="rounded-2xl bg-slate-950 p-4 text-white">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                            Builder flow
                        </div>

                        <div className="mt-3 space-y-2 text-xs text-slate-300">
                            <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                Edit Solidity source
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                Compile and review warnings
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                                Run preflight before signing
                            </div>
                        </div>
                    </div>

                    {disabled ? (
                        <button
                            type="button"
                            disabled
                            className="mt-4 w-full rounded-2xl bg-slate-300 px-5 py-3 text-center text-sm font-semibold text-slate-600 shadow-sm"
                        >
                            Open Custom Builder
                        </button>
                    ) : (
                        <Link
                            to={`/custom-builder?${search}`}
                            state={state}
                            className="mt-4 block w-full rounded-2xl bg-[#050713] px-5 py-3 text-center text-sm font-semibold text-white shadow-[0_16px_36px_rgba(15,23,42,0.22)] transition hover:-translate-y-0.5 hover:bg-black"
                        >
                            Open Custom Builder
                        </Link>
                    )}

                    <p className="mt-3 text-center text-[11px] leading-5 text-slate-500">
                        Your wallet signs only after compile, preflight, and final review.
                    </p>
                </div>
            </div>
        </section>
    );
}